import 'vite/modulepreload-polyfill';
import { fireInitialTracking, fireSessionLandingPage } from '@/composables/gtm/initialTracking';
import { useCartUrlParams } from '@/composables/useCartUrlParams';

declare global {
  interface Window {
    shopifyLocalization: {
      isoCode: string;
    };
    collectionArray: RawCollection[];
    productArray: ShopifyProductData[];
    nestedProducts: ShopifyProductData[];
    bundleProducts: ShopifyProductData[];
    displayProducts: ProductComplete[];
    pageArray: RawPage[];
    defaultMeta: MetaOptions;
    shopAttributes: ShopAttributes;
    customerAttributes: rawCustomerAttributes;
    Shopify: {
      captcha: {
        protect: (element: HTMLElement) => void;
      };
      routes?: {
        root: string;
      };
    };
    // Shared in-flight geo lookups. See frontend/composables/geo.ts.
    __geoPromise?: Promise<{ country_code?: string; ip?: string } | null>;
    __geoEndpoint?: string;
    __shopifyGeoPromise?: Promise<{ detected_values?: { country?: { handle?: string } } } | null>;
  }
}

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@unhead/vue';
import { useCollectionStore } from '@/features/Collection/stores/collection';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductBundleStore } from '@/features/Product/stores/productBundle';
import { plugin, defaultConfig } from '@formkit/vue';

import App from '@/components/App.vue';
import { ShopAttributes } from '@/components/models';
import { MetaOptions } from '@/composables/useMeta.ts';
import { useSystemStore } from '@/stores/system.ts';
import { RawPage, usePageStore } from '@/features/Page/stores/page';
import { RawCollection } from '@/features/Collection/types/models';
import { ProductComplete, ShopifyProductData } from '@/features/Product/types/models';
import { rawCustomerAttributes } from '@/features/Account/types/models';
import { useAccountStore } from '@/features/Account/stores/account';
import router from '@/modules/router';
import { interceptInternalLinks } from '@/composables/internalLinkHelpers';
import { gtmEvent, mountGtm } from '@/composables/gtm/useGtm';
import { useCartStore } from '@/features/Cart/stores/cart';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { readProductStock } from '@/features/Product/services/productStock';
import { useProductStockRefresh } from '@/features/Product/composables/useProductStockRefresh';
import { useCartStockValidation } from '@/features/Cart/composables/useCartStockValidation';
import { cartActions } from '@/features/Cart/services/cartService';
import { useModalStore } from '@/features/UserInterface/stores/modal';
import { useBrowserStore } from '@/stores/browser';
import { useProductPageDetails } from '@/features/Product/stores/productPageDetails';
import { formClasses } from '@/features/Form/services/formClassHelpers';

const app = createApp(App);

app.use(createPinia());

const { handleCartUrlParams, hasCartUrlParams } = useCartUrlParams();

if (window.shopAttributes) {
  useSystemStore().setShopAttributes(window.shopAttributes);
}
if (window.collectionArray) {
  useCollectionStore().setCachedCollections(window.collectionArray);
}
if (window.productArray) {
  useProductBaseStore().setCachedBaseProducts(window.productArray);
}
if (window.pageArray) {
  usePageStore().setCachedPages(window.pageArray);
}
if (window.customerAttributes) {
  useAccountStore().setRawCustomerAttributes(window.customerAttributes);
}
if (window.displayProducts) {
  useProductCompleteStore().setCachedDisplayProducts(window.displayProducts);
}
if (window.bundleProducts) {
  useProductBundleStore().setCachedBundleProducts(window.bundleProducts);
}
if (window.nestedProducts) {
  useProductBaseStore().setCachedNestedProducts(window.nestedProducts);
}
const productStock = readProductStock(document);
if (productStock) {
  useProductStockStore().setStock(productStock);
}
useCartStore();
useModalStore();
useBrowserStore().initializeBrowserData();
useProductPageDetails();

app.use(router);

interceptInternalLinks(router);
useProductStockRefresh(router);
useCartStockValidation();

app.use(
  plugin,
  defaultConfig({
    config: {
      classes: formClasses,
    },
  }),
);

const head = createHead();
app.use(head);
app.use(mountGtm());

app.mount('#app');

gtmEvent().mountApp();

fireSessionLandingPage();
fireInitialTracking();

if (hasCartUrlParams()) {
  handleCartUrlParams().finally(() => {
    cartActions().refreshCart();
  });
} else {
  cartActions().refreshCart();
}
